import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiPost } from '../utils/apiUtils';
import SaveFilterDialog from './dialogs/SaveFilterDialog';

/**
 * ConfigureFilters component - allows configuration of filter parameters and column-specific rules
 */
const ConfigureFilters = ({
  onNext,
  onBack,
  setLoading,
  fileData,
  selectedColumns,
  filterConfig,
  setFilterConfig,
  filterParams,
  setFilterParams,
  setFilterId
}) => {
  const [error, setError] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  // State to track the current filter ID when using saved filters
  const [currentFilterId, setCurrentFilterId] = useState(null);

  // Set default model when component mounts and check for saved filter config
  useEffect(() => {
    // Check if we have a saved filter configuration in localStorage
    const savedFilterConfig = localStorage.getItem('savedFilterConfig');
    const applyingSavedFilter = localStorage.getItem('applyingSavedFilter');
    
    console.log('Checking for saved filter config:', { savedFilterConfig, applyingSavedFilter });
    
    if (savedFilterConfig && applyingSavedFilter === 'true') {
      try {
        // Parse the saved filter configuration
        const parsedConfig = JSON.parse(savedFilterConfig);
        console.log('Loaded saved filter config:', parsedConfig);
        
        // Set the filter parameters from the saved configuration
        setFilterParams({
          model: parsedConfig.model || 'o4-mini-high',
          reasoningText: parsedConfig.reasoningText || '',
          chunkSize: parsedConfig.chunkSize || 300
        });
        
        // Check if we have a properly structured columnConfig in the standardized format
        if (parsedConfig.columnConfig && Object.keys(parsedConfig.columnConfig).length > 0) {
          console.log('Using standardized columnConfig from filter:', parsedConfig.columnConfig);
          setFilterConfig(parsedConfig.columnConfig);
          
          // Extract the column names for logging purposes
          const configuredColumns = Object.keys(parsedConfig.columnConfig);
          console.log('Columns configured in standardized format:', configuredColumns);
        }
        
        // Also check if there are selected columns stored separately in localStorage
        // for better cross-component communication
        const selectedColumnsJSON = localStorage.getItem('selectedColumns');
        if (selectedColumnsJSON) {
          try {
            const selectedColumns = JSON.parse(selectedColumnsJSON);
            console.log('Found selected columns in localStorage:', selectedColumns);
            // We don't set them directly here since selectedColumns is passed as a prop
            // This is just for logging purposes
          } catch (e) {
            console.error('Error parsing selected columns from localStorage:', e);
          }
        }
        
        // Clear the flags after we've applied the configuration
        localStorage.removeItem('applyingSavedFilter');
      } catch (error) {
        console.error('Error loading saved filter config:', error);
      }
    } else {
      // If no saved filter config, set default values
      if (!filterParams || typeof filterParams !== 'object') {
        setFilterParams({
          model: 'o4-mini-high',
          reasoningText: '',
          chunkSize: 300
        });
      } else if (filterParams.model !== 'o4-mini-high') {
        setFilterParams(prev => ({
          ...prev,
          model: 'o4-mini-high'
        }));
      }
    }
  }, [filterParams, setFilterParams, setFilterConfig]); // Add proper dependencies
  
  // Models available for filtering (matches backend configuration)
  const modelOptions = [
    // NEW! April 2025 Models (latest reasoning models)
    { value: 'o3', label: 'O3 - Flagship reasoning model with best-in-class performance' },
    { value: 'o4-mini', label: 'O4 Mini - Fast, powerful reasoning model with strong math & coding' },
    { value: 'o4-mini-high', label: 'O4 Mini High - Enhanced reliability with extra compute', default: true },
    
    // Previous reasoning models (o-series)
    { value: 'o3-mini', label: 'O3 Mini - Fast, flexible reasoning model' },
    { value: 'o1', label: 'O1 - High-intelligence reasoning model' },
    { value: 'o1-mini', label: 'O1 Mini - Faster, affordable reasoning model' },
    { value: 'o1-pro', label: 'O1 Pro - Enhanced O1 with more compute' },
    
    // Flagship chat models
    { value: 'gpt-4.1', label: 'GPT-4.1 - Flagship for complex tasks' },
    { value: 'gpt-4o', label: 'GPT-4o - Fast, intelligent, flexible' },
    { value: 'chatgpt-4o-latest', label: 'ChatGPT-4o - Latest ChatGPT model' },
    
    // Cost-optimized models
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini - Balanced for cost/performance' },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano - Fastest, most cost-effective' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini - Fast, affordable small model' }
  ];

  // Handle changes to the main filter parameters
  const handleParamChange = (param) => (event) => {
    const value = event.target.value;
    setFilterParams(prev => ({
      ...prev,
      [param]: value
    }));
    
    // Save reasoning text to localStorage when it changes
    if (param === 'reasoningText') {
      console.log('Saving reasoning text to localStorage:', value.substring(0, 50) + '...');
      localStorage.setItem('filterReasoningText', value);
    }
  };

  // Temperature is no longer used - o3 models don't support it

  // Chunk size is now fixed at 300 - removing user control to simplify UI

  // Handle changes to column-specific configuration
  const handleColumnConfigChange = (column, param) => (event) => {
    const updatedConfig = { ...filterConfig };
    
    // Ensure we're using the standardized columnConfig structure
    if (!updatedConfig[column]) {
      updatedConfig[column] = {};
    }
    
    updatedConfig[column][param] = event.target.value;
    setFilterConfig(updatedConfig);
    
    // CRITICAL FIX: Save the updated filterConfig to localStorage
    // This ensures column instructions are preserved between steps
    try {
      // Get the existing state from localStorage
      const userId = localStorage.getItem('currentUserId') || 'anonymous';
      const storageKey = `filterToolState_${userId}`;
      const existingState = JSON.parse(localStorage.getItem(storageKey)) || {};
      
      // Update just the filterConfig part
      localStorage.setItem(storageKey, JSON.stringify({
        ...existingState,
        filterConfig: updatedConfig
      }));
      
      // Save standardized columnConfig separately for extra reliability
      localStorage.setItem('filterColumnConfig', JSON.stringify(updatedConfig));
      
      console.log(`Saved updated filterConfig to localStorage with column instructions for ${column}: "${updatedConfig[column][param].substring(0, 30)}${updatedConfig[column][param].length > 30 ? '...' : ''}"`);
    } catch (e) {
      console.error('Error saving filterConfig to localStorage:', e);
    }
    
    console.log(`Updated column config for ${column}.${param} to: ${event.target.value.substring(0, 30)}${event.target.value.length > 30 ? '...' : ''}`);
  };

  // Handle resetting all data
  const handleReset = () => {
    setResetDialogOpen(true);
  };

  // Confirm and execute reset
  const confirmReset = () => {
    // Reset all application state
    setFilterConfig({});
    setFilterParams(prev => ({
      ...prev,
      reasoningText: '',
      model: 'o4-mini-high'
    }));
    setResetDialogOpen(false);
    
    // Navigate back to upload screen
    onBack();
    console.log('All data has been reset');
  };

  // Cancel reset
  const cancelReset = () => {
    setResetDialogOpen(false);
  };

  // Start the filtering process
  const handleApplyFilter = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Debug: Log fileData details to check if it exists and has the expected id property
      console.log('DEBUG - fileData:', fileData);
      if (fileData) {
        console.log('DEBUG - fileData.id:', fileData.id);
      } else {
        console.error('DEBUG - fileData is undefined or null!');
      }
      
      if (!selectedColumns || selectedColumns.length === 0) {
        throw new Error('Please select at least one column to filter');
      }
      
      if (!filterParams.reasoningText.trim()) {
        throw new Error('Please provide guidance text for the filter');
      }
      
      // Store reasoning text in localStorage for persistence
      localStorage.setItem('filterReasoningText', filterParams.reasoningText);
      
      // Create a standardized filter configuration with columnConfig structure
      const standardizedColumnConfig = {};
      
      // Ensure every selected column has an entry in columnConfig
      selectedColumns.forEach(column => {
        // Keep any existing column configuration or create a new one
        standardizedColumnConfig[column] = filterConfig[column] || { instructions: '' };
        console.log(`Setting up column config for ${column}:`, standardizedColumnConfig[column]);
      });
      
      // Log the standardized configuration
      console.log('Standardized columnConfig:', standardizedColumnConfig);
      
      // CRITICAL FIX: Save the standardized column config to localStorage
      // This ensures column instructions are preserved between components
      // Use the same pattern as reasoningText which we know works
      localStorage.setItem('filterColumnConfig', JSON.stringify(standardizedColumnConfig));
      
      // DIAGNOSTIC LOG: Specifically verify column instructions are saved
      console.log('DIAGNOSTIC CHECK - Column instructions saved to localStorage:');
      selectedColumns.forEach(column => {
        if (standardizedColumnConfig[column] && standardizedColumnConfig[column].instructions) {
          console.log(`  - ${column}: "${standardizedColumnConfig[column].instructions}"`);
        } else {
          console.log(`  - ${column}: <no instructions>`);
        }
      });
      
      // Verify it can be retrieved properly
      try {
        const testRetrieve = JSON.parse(localStorage.getItem('filterColumnConfig'));
        console.log('DIAGNOSTIC: Retrieved from localStorage successfully:', testRetrieve);
      } catch(e) {
        console.error('DIAGNOSTIC: Error retrieving from localStorage', e);
      }
      
      // Check if we're in saved filter mode and should reuse the existing filter ID
      const applySavedFilterMode = localStorage.getItem('applyingSavedFilter') === 'true';
      const existingFilterId = localStorage.getItem('currentFilterId');
      const usingExistingFilterId = applySavedFilterMode && existingFilterId;
      
      console.log('JOB-ID-DEBUG:', `Checking for saved filter mode:`, {
        applySavedFilterMode,
        existingFilterId,
        usingExistingFilterId
      });
      
      // If there's an existing filter ID from a saved filter, capture it in our state
      if (existingFilterId && !currentFilterId) {
        console.log('JOB-ID-DEBUG:', `Capturing existing filter ID: ${existingFilterId} in component state`);
        setCurrentFilterId(existingFilterId);
      }
      
      // If we're applying a saved filter or we have a current filter ID, use that instead of creating a new job
      if (usingExistingFilterId || currentFilterId) {
        const filterIdToUse = currentFilterId || existingFilterId;
        console.log('JOB-ID-DEBUG:', `Using existing filter ID: ${filterIdToUse} instead of creating a new job`);
        
        // Set the filter ID and move to results page without creating a new job
        setFilterId(filterIdToUse);
        setLoading(false);
        onNext();
        return; // Exit early - we're reusing the existing job
      }
      
      console.log('Applying filter with parameters:', {
        fileId: fileData?.id,
        selectedColumns,
        reasoningText: filterParams.reasoningText,
        model: filterParams.model,
        columnConfig: standardizedColumnConfig
      });
      
      // Check if fileData exists and has an id before making the API call
      if (!fileData || !fileData.id) {
        throw new Error('File data is missing or incomplete. Please upload your file again.');
      }
      
      const response = await apiPost('/api/filter', {
        fileId: fileData.id,
        selectedColumns,
        reasoningText: filterParams.reasoningText,
        model: filterParams.model,
        chunkSize: parseInt(filterParams.chunkSize, 10) || 300,
        filterConfig: { columnConfig: standardizedColumnConfig } // Match backend's expected structure
      });
      
      if (!response.ok) {
        // Handle error responses
        try {
          const errorData = await response.json();
          // Check if error is an object or string and handle accordingly
          if (typeof errorData.error === 'object') {
            throw new Error(JSON.stringify(errorData.error) || `Filter failed with status: ${response.status}`);
          } else {
            throw new Error(errorData.error || `Filter failed with status: ${response.status}`);
          }
        } catch (jsonError) {
          // If JSON parsing fails, use the status text
          throw new Error(`Filter failed: ${response.statusText || response.status}`);
        }
      }
      
      // Parse the successful response
      const responseData = await response.json();
      
      // Use the new filter ID from the response
      const newFilterId = responseData.filterId;
      setFilterId(newFilterId);
      
      // Check for saved overrides to transfer to the new filter job
      const savedOverrides = localStorage.getItem('savedFilterOverrides');
      if (savedOverrides) {
        try {
          const overridesObj = JSON.parse(savedOverrides);
          if (overridesObj && Object.keys(overridesObj).length > 0) {
            console.log('OVERRIDES-TRANSFER: Found saved overrides to transfer to new filter job:', overridesObj);
            
            // We need to transform the overrides object into individual API calls
            // The overridesObj format is {column: {value: decision}}
            // But the API expects {filterId, overrides: {column, value, decision}}
            
            let allOverrideSuccessful = true;
            let overridePromises = [];
            let overrideDetails = [];
            
            console.log('OVERRIDES-TRANSFER: Starting override transfer for columns:', Object.keys(overridesObj));
            
            // Process each column's overrides
            for (const column in overridesObj) {
              const columnOverrides = overridesObj[column];
              
              // Process each value override within the column
              for (const value in columnOverrides) {
                const decision = columnOverrides[value];
                
                console.log(`OVERRIDES-TRANSFER: Preparing to apply override for ${column}/${value} with decision ${decision}`);
                
                // Create a promise for this override
                const overridePromise = (async () => {
                  try {
                    const overrideData = {
                      filterId: newFilterId,
                      overrides: {
                        column,
                        value,
                        decision
                      }
                    };
                    
                    console.log(`OVERRIDES-TRANSFER: Sending override request for ${column}/${value}:`, overrideData);
                    
                    const overrideResponse = await apiPost(`/api/overrides`, overrideData);
                    
                    if (overrideResponse.ok) {
                      console.log(`OVERRIDES-TRANSFER: Successfully applied override for ${column}/${value}`);
                      return { success: true, column, value, decision };
                    } else {
                      const errorText = await overrideResponse.text();
                      console.error(`OVERRIDES-TRANSFER: Error applying override for ${column}/${value}:`, errorText);
                      return { success: false, column, value, decision, error: errorText };
                    }
                  } catch (overrideError) {
                    console.error(`OVERRIDES-TRANSFER: Exception applying override for ${column}/${value}:`, overrideError);
                    return { success: false, column, value, decision, error: overrideError };
                  }
                })();
                
                overridePromises.push(overridePromise);
                overrideDetails.push({ column, value, decision });
              }
            }
            
            // Wait for all override requests to complete
            console.log(`OVERRIDES-TRANSFER: Waiting for ${overridePromises.length} override requests to complete`);
            const results = await Promise.all(overridePromises);
            
            // Check if all overrides were successful
            const failedOverrides = results.filter(r => !r.success);
            allOverrideSuccessful = failedOverrides.length === 0;
            
            if (failedOverrides.length > 0) {
              console.error(`OVERRIDES-TRANSFER: ${failedOverrides.length} overrides failed:`, 
                failedOverrides.map(f => `${f.column}/${f.value}`));
            }
            
            if (allOverrideSuccessful) {
              console.log(`OVERRIDES-TRANSFER: Successfully transferred all overrides to new filter job ${newFilterId}`);
              
              // Mark that overrides have been applied to this filter
              localStorage.setItem('currentFilterIdWithOverrides', newFilterId);
              localStorage.setItem('overridesApplied', 'true');
            } else {
              console.error('OVERRIDES-TRANSFER: Some overrides failed to transfer to the new filter job');
            }
          }
        } catch (err) {
          console.error('OVERRIDES-TRANSFER: Error transferring overrides:', err);
        }
      }
      
      // Store this filter ID in our component state to reuse it later
      // This avoids creating multiple job IDs for the same filtering session
      setCurrentFilterId(newFilterId);
      console.log('JOB-ID-DEBUG:', `Setting currentFilterId to new filter ID: ${newFilterId}`);
      
      setLoading(false);
      
      // Move to results page
      onNext();
    } catch (error) {
      setLoading(false);
      console.error('Filter error:', error);
      
      // Ensure we're displaying a string, not an object
      let errorMessage = 'An error occurred while applying filters';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'object') {
        // If error is an object, stringify it for display
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Unknown error occurred';
        }
      }
      
      setError(errorMessage);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header removed to avoid redundancy with page title */}
      <Typography variant="body1" sx={{ mb: 3 }}>
        Tell the model who you want to target, then fine‑tune by column
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
        <Typography variant="h6" gutterBottom>
          Global context
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Applied to every row
        </Typography>
        
        {/* Filter Instructions */}
        <TextField
          label="Global Filter Instructions"
          multiline
          rows={4}
          value={filterParams.reasoningText}
          onChange={handleParamChange('reasoningText')}
          placeholder="Pitch our Kubernetes platform to senior SRE / DevOps leaders."
          fullWidth
          margin="normal"
        />
        
        <Box sx={{ display: 'flex', gap: 3, mt: 3, flexWrap: 'wrap' }}>
          {/* Model Selection */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="model-select-label">Model</InputLabel>
            <Select
              labelId="model-select-label"
              value={filterParams.model || 'o4-mini-high'}
              onChange={handleParamChange('model')}
              label="Model"
              sx={{ mb: 1 }}
            >
              {modelOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Temperature removed - not used for o3-mini and other reasoning models */}

          {/* Chunk Size removed - fixed at 300 for optimal performance */}
        </Box>
      </Paper>
      
      <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
        <Typography variant="h6" gutterBottom>
          Column rules
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add a rule to narrow a single column.
        </Typography>
        
        {selectedColumns.length > 0 ? (
          <Box>
            {selectedColumns.map(column => (
              <Accordion key={column} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{column}</Typography>
                  {filterConfig[column] && Object.keys(filterConfig[column]).length > 0 ? (
                    <Chip 
                      label="Rule" 
                      size="small" 
                      color="primary" 
                      sx={{ ml: 2 }} 
                    />
                  ) : (
                    <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>▸ Rule</Typography>
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={filterConfig[column]?.instructions || ''}
                    onChange={handleColumnConfigChange(column, 'instructions')}
                    variant="outlined"
                    placeholder={column.toLowerCase().includes('title') ? 
                      'e.g., Titles that manage Kubernetes workloads' : 
                      column.toLowerCase().includes('size') || column.toLowerCase().includes('company') ? 
                      'e.g., 50-1,000 employees' : 
                      `e.g., Specific criteria for ${column}`}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" align="center">
            No columns selected. Please go back and select columns to filter.
          </Typography>
        )}
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button 
          onClick={onBack}
          variant="outlined" 
          sx={{ 
            borderColor: 'rgba(255,255,255,0.3)',
            color: 'text.secondary',
            textTransform: 'none',
            borderRadius: '4px'  
          }}
        >
          Previous
        </Button>
        
        {/* New Reset All Data button */}
        <Button 
          variant="outlined" 
          color="secondary"
          onClick={handleReset}
          startIcon={<RefreshIcon />}
          sx={{ 
            textTransform: 'none',
            borderRadius: '4px'
          }}
        >
          Reset
        </Button>
        
        <Box>
          <Tooltip title="Save this filter configuration for future use">
            <Button 
              startIcon={<SaveIcon />}
              onClick={() => setSaveDialogOpen(true)} 
              sx={{ mr: 2 }}
              disabled={!filterParams.reasoningText.trim() || selectedColumns.length === 0}
            >
              Save
            </Button>
          </Tooltip>
          <Button 
            variant="contained" 
            onClick={handleApplyFilter} 
            disabled={!filterParams.reasoningText.trim() || selectedColumns.length === 0}
            sx={{ minWidth: 100 }}
          >
            Apply
          </Button>
        </Box>
      </Box>
      
      {/* Save Filter Dialog */}
      <SaveFilterDialog 
        open={saveDialogOpen} 
        onClose={(success) => {
          setSaveDialogOpen(false);
          if (success) {
            // Show success message or notification
            setError(null); // Clear any errors
          }
        }} 
        filterConfig={{
          selectedColumns,
          reasoningText: filterParams.reasoningText,
          model: filterParams.model,
          temperature: filterParams.temperature || 0.7,
          chunkSize: parseInt(filterParams.chunkSize, 10) || 300,
          // Ensure we're using the standardized columnConfig structure with proper instructions
          columnConfig: Object.fromEntries(
            selectedColumns.map(column => {
              // Ensure we have proper instructions for this column
              const columnData = filterConfig[column] || {};
              return [
                column, 
                { instructions: columnData.instructions || '' }
              ];
            })
          )
        }}
      />
      
      {/* Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={cancelReset}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">
          Reset?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            This will clear all your filter configurations and selections. You'll need to start over from the beginning. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelReset} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmReset} color="error" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfigureFilters;
